import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { Mail, Pencil, Plus } from "lucide-react";
import { message, type TableColumnsType } from "antd";
import AntDTable from "@/common/components/ui/antd-table";
import { api } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { ManageAccessModal } from "./components/ManageAccessModal";
import { NewUserModal } from "./components/NewUserModal";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import { cn } from "@/lib/utils";
import { RM } from "./components/theme";

interface RoleRow {
  id: number;
  name: string;
}

interface StaffRow {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role_name: string;
  role_id: number | null;
  is_active_admin: boolean;
  mobile: string;
  date_joined: string;
  is_superuser: boolean;
  branch?: string;
}

const MOCK_STAFF: StaffRow[] = [
  {
    id: 1,
    username: "rajesh.kumar",
    email: "rajesh.kumar@finease.com",
    first_name: "Rajesh",
    last_name: "Kumar",
    role_name: "Super Admin",
    role_id: 1,
    is_active_admin: true,
    mobile: "9876543210",
    date_joined: "2024-01-15T10:00:00Z",
    is_superuser: true,
    branch: "Delhi Central",
  },
  {
    id: 2,
    username: "priya.sharma",
    email: "priya.sharma@finease.com",
    first_name: "Priya",
    last_name: "Sharma",
    role_name: "Admin",
    role_id: 2,
    is_active_admin: true,
    mobile: "9876543211",
    date_joined: "2024-02-01T09:30:00Z",
    is_superuser: false,
    branch: "New York",
  },
  {
    id: 3,
    username: "amit.verma",
    email: "amit.verma@finease.com",
    first_name: "Amit",
    last_name: "Verma",
    role_name: "Admin",
    role_id: 2,
    is_active_admin: true,
    mobile: "9876543212",
    date_joined: "2024-02-20T14:15:00Z",
    is_superuser: false,
    branch: "San Francisco",
  },
  {
    id: 4,
    username: "sneha.rao",
    email: "sneha.rao@finease.com",
    first_name: "Sneha",
    last_name: "Rao",
    role_name: "MMTC",
    role_id: 3,
    is_active_admin: true,
    mobile: "9876543213",
    date_joined: "2024-03-05T11:45:00Z",
    is_superuser: false,
    branch: "MMTC",
  },
  {
    id: 5,
    username: "vikram.singh",
    email: "vikram.singh@finease.com",
    first_name: "Vikram",
    last_name: "Singh",
    role_name: "Admin",
    role_id: 4,
    is_active_admin: true,
    mobile: "9876543214",
    date_joined: "2024-03-10T09:00:00Z",
    is_superuser: false,
    branch: "MMTC",
  },
  {
    id: 6,
    username: "anita.desai",
    email: "anita.desai@finease.com",
    first_name: "Anita",
    last_name: "Desai",
    role_name: "Admin",
    role_id: 5,
    is_active_admin: true,
    mobile: "9876543215",
    date_joined: "2024-01-20T08:00:00Z",
    is_superuser: false,
    branch: "Bangalore",
  },
  {
    id: 7,
    username: "rohan.mehta",
    email: "rohan.mehta@finease.com",
    first_name: "Rohan",
    last_name: "Mehta",
    role_name: "Admin",
    role_id: 7,
    is_active_admin: false,
    mobile: "9876543216",
    date_joined: "2024-04-01T12:00:00Z",
    is_superuser: false,
    branch: "Delhi Central",
  },
];

const MOCK_ROLES: RoleRow[] = [
  { id: 1, name: "Super Admin" },
  { id: 2, name: "Admin" },
  { id: 3, name: "Admin" },
  { id: 4, name: "Admin" },
  { id: 5, name: "Admin" },
  { id: 6, name: "mmtc" },
  { id: 7, name: "mmtc" },
];

export default function UsersPage() {
  const [staff, setStaff] = useState<StaffRow[]>(MOCK_STAFF);
  const [roles, setRoles] = useState<RoleRow[]>(MOCK_ROLES);
  const [loading, setLoading] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<StaffRow | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessTitle, setAccessTitle] = useState("User permissions");
  const { hasPermission } = usePermissions();

  const fetchData = async () => {
    try {
      const [staffRes, rolesRes] = await Promise.all([
        api.rbac.staff.list(),
        api.rbac.roles.list(),
      ]);
      if (staffRes.data && staffRes.data.length > 0) {
        setStaff(staffRes.data as StaffRow[]);
      }
      if (rolesRes.data && rolesRes.data.length > 0) {
        const r = rolesRes.data as { id: number; name: string }[];
        setRoles(r.map(x => ({ id: x.id, name: x.name })));
      }
    } catch {
      // keep mock
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalLabel = useMemo(() => {
    const n = staff.length;
    return `Total User : ${String(n).padStart(2, "0")}`;
  }, [staff.length]);

  const columns: TableColumnsType<StaffRow> = useMemo(
    () => [
      {
        title: "User ID",
        key: "userId",
        width: 96,
        render: (_, __, index) => (
          <span className={cn("font-medium", RM.tableCell)}>
            {String((index ?? 0) + 1).padStart(2, "0")}
          </span>
        ),
      },
      {
        title: "User",
        key: "user",
        render: (_, u) => (
          <div>
            <div className={cn("font-semibold", RM.tableCell)}>
              {u.first_name} {u.last_name}
            </div>
            <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <Mail className="h-3 w-3 shrink-0 text-brandPrimary/80" />
              {u.email}
            </div>
          </div>
        ),
      },
      {
        title: "Role",
        key: "role",
        render: (_, u) => <span className={RM.rolePill}>{u.role_name}</span>,
      },
      {
        title: "Status",
        key: "status",
        render: (_, u) => (
          <span
            className={cn(
              "inline-flex rounded-full px-3 py-1 text-xs font-semibold",
              u.is_active_admin ? RM.statusActive : RM.statusInactive
            )}
          >
            {u.is_active_admin ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        title: "Permission",
        key: "permission",
        render: (_, u) => (
          <Button
            variant="outline"
            size="sm"
            className={RM.outlineAccent}
            onClick={() => {
              setAccessTitle(`${u.first_name} ${u.last_name} — Permissions`);
              setAccessOpen(true);
            }}
          >
            Manage
          </Button>
        ),
      },
      {
        title: "Client Status",
        key: "clientStatus",
        align: "center" as const,
        render: (_, u) => (
          <button
            type="button"
            className="rounded-lg p-2 text-muted-foreground hover:bg-brandPrimary/10 hover:text-brandDark"
            aria-label="Edit user"
            onClick={() => {
              setEditingUser(u);
              setNewUserOpen(true);
            }}
          >
            <Pencil className="h-4 w-4" />
          </button>
        ),
      },
    ],
    []
  );

  // const downloadReport = () => {
  //   const header = [
  //     "User ID",
  //     "Name",
  //     "Email",
  //     "Role",
  //     "Branch",
  //     "Status",
  //   ].join(",");
  //   const lines = staff.map((s, i) =>
  //     [
  //       String(i + 1).padStart(2, "0"),
  //       `"${s.first_name} ${s.last_name}"`,
  //       s.email,
  //       s.role_name,
  //       s.branch ?? "",
  //       s.is_active_admin ? "Active" : "Inactive",
  //     ].join(",")
  //   );
  //   const blob = new Blob([[header, ...lines].join("\n")], {
  //     type: "text/csv;charset=utf-8",
  //   });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = `user-management-${new Date().toISOString().slice(0, 10)}.csv`;
  //   a.click();
  //   URL.revokeObjectURL(url);
  //   message.success("Report downloaded");
  // };

  return (
    <div className={RM.page}>
      <div className={RM.container}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className={RM.h1}>User Management</h1>
          <div className="flex flex-wrap items-center gap-3">
            <span className={RM.statBadge}>{totalLabel}</span>
            {hasPermission("admin-management:staff-management:create") && (
              <Button
                className={RM.btnPrimary}
                onClick={() => {
                  setEditingUser(null);
                  setNewUserOpen(true);
                }}
              >
                <Plus className="h-4 w-4" /> New User
              </Button>
            )}
          </div>
        </div>

        <div className={RM.card}>
          <AntDTable<StaffRow>
            columns={columns}
            data={staff}
            rowKey="id"
            loading={loading}
            scroll={{ x: "max-content" }}
            headerBg="rgba(102, 160, 205, 0.08)"
            primaryColor="#66A0CD"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
            }}
            paginationStyle={{
              paddingLeft: "0",
              paddingRight: "0",
            }}
          />
        </div>

        <p className={RM.footnote}>
          Manage roles in{" "}
          <Link
            className={RM.link}
            to={finEaseHomeNavigationRoutes.ROLE_MANAGEMENT_ROLES}
          >
            Roles
          </Link>
          .
        </p>
      </div>

      <NewUserModal
        open={newUserOpen}
        onOpenChange={open => {
          setNewUserOpen(open);
          if (!open) {
            setEditingUser(null);
          }
        }}
        roleOptions={roles}
        isEditMode={!!editingUser}
        initialData={
          editingUser
            ? {
                userId: editingUser.username ?? "",
                firstName: editingUser.first_name ?? "",
                middleName: "",
                lastName: editingUser.last_name ?? "",
                roleId: editingUser.role_id ? String(editingUser.role_id) : "",
                mobile: editingUser.mobile ?? "",
                email: editingUser.email ?? "",
              }
            : null
        }
        onViewPermission={() => {
          setAccessTitle("New user — permissions");
          setAccessOpen(true);
        }}
        onCreated={fetchData}
      />

      <ManageAccessModal
        open={accessOpen}
        onOpenChange={setAccessOpen}
        title={accessTitle}
        onSave={() => message.success("Permissions saved (demo)")}
      />
    </div>
  );
}
